import { ChevronDown, Linkedin, MapPin, User } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { linkedinGetProfiles } from 'sprites-types/workflow/tools/linkedinGetProfiles'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function ProfileItem({ profile, className }: { profile: any; className?: string }) {
  const firstName = profile.firstName || profile.first_name || ''
  const lastName = profile.lastName || profile.last_name || ''
  const fullName = profile.name || `${firstName} ${lastName}`.trim() || 'LinkedIn User'
  const headline = profile.headline || profile.title || profile.position || ''
  const location = profile.location || profile.city || profile.region || ''
  const summary = profile.summary || profile.description || profile.bio || ''
  const profilePic = profile.profilePic || profile.photoUrl || profile.picture || profile.avatar
  const publicUrl = profile.publicUrl || profile.linkedinUrl || profile.url
  const company = profile.company || profile.currentCompany || ''
  const connections = profile.connections || profile.connectionCount || 0

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          {profilePic ? (
            <Image
              src={profilePic}
              alt={`${fullName} profile`}
              width={48}
              height={48}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
              <User className="h-6 w-6 text-blue-600" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="mb-1 text-sm font-semibold text-gray-900">{fullName}</div>

            {headline && <div className="mb-2 line-clamp-2 text-xs text-gray-700">{headline}</div>}

            {company && <div className="mb-1 text-xs text-gray-600">at {company}</div>}

            <div className="mb-2 flex items-center gap-3 text-xs text-gray-600">
              {location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span>{location}</span>
                </div>
              )}
              {connections > 0 && <div>{formatNumber(connections)} connections</div>}
            </div>

            {summary && <div className="mb-2 line-clamp-2 text-xs text-gray-700">{summary}</div>}

            {publicUrl && (
              <div className="flex items-center gap-3">
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <Linkedin className="h-3 w-3" />
                  View Profile
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function LinkedinGetProfilesDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(linkedinGetProfiles, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const profiles = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.profiles ||
      (result as any)?.people ||
      (result ? [result] : [])

  if (profiles.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <User className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No LinkedIn profiles found</div>
        </div>
      </div>
    )
  }

  if (profiles.length === 1) {
    return <ProfileItem profile={profiles[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Linkedin className="h-5 w-5 text-blue-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">LinkedIn Profiles</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {profiles.length} profiles retrieved
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {profiles.map((profile: any, index: number) => (
            <ProfileItem
              key={profile.id || profile.publicUrl || index}
              profile={profile}
              className="mx-8"
            />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadLinkedinGetProfiles(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function linkedinGetProfilesToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(linkedinGetProfiles, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data

  const profiles = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.profiles ||
      (result as any)?.people ||
      (result ? [result] : [])

  if (profiles.length === 0) {
    return `## LinkedIn Profiles

No profiles found.

---
`
  }

  const tableHeader = `| Name | Headline | Company | Location | Connections | Profile Link |
|------|----------|---------|----------|-------------|--------------|`

  const tableRows = profiles
    .map((profile: any) => {
      const firstName = profile.firstName || profile.first_name || ''
      const lastName = profile.lastName || profile.last_name || ''
      const fullName = profile.name || `${firstName} ${lastName}`.trim() || 'LinkedIn User'
      const headline = (profile.headline || profile.title || profile.position || '-')
        .replace(/\|/g, '\\|')
        .replace(/\n/g, ' ')
        .slice(0, 50)
      const headlineDisplay = headline.length === 50 ? headline + '...' : headline
      const company = profile.company || profile.currentCompany || '-'
      const location = profile.location || profile.city || profile.region || '-'
      const connections = profile.connections || profile.connectionCount || 0
      const connectionsDisplay = connections > 0 ? connections.toLocaleString() : '-'
      const publicUrl = profile.publicUrl || profile.linkedinUrl || profile.url
      const profileLink = publicUrl ? `[View](${publicUrl})` : '-'

      return `| ${fullName} | ${headlineDisplay} | ${company} | ${location} | ${connectionsDisplay} | ${profileLink} |`
    })
    .join('\n')

  const detailedProfiles = profiles
    .filter((profile: any) => (profile.summary || profile.description || profile.bio)?.length > 100)
    .slice(0, 5) // Limit to first 5 detailed summaries
    .map((profile: any) => {
      const firstName = profile.firstName || profile.first_name || ''
      const lastName = profile.lastName || profile.last_name || ''
      const fullName = profile.name || `${firstName} ${lastName}`.trim() || 'LinkedIn User'
      const summary = profile.summary || profile.description || profile.bio || ''
      return `### ${fullName}\n\n${summary}`
    })
    .join('\n\n')

  return `## LinkedIn Profiles (${profiles.length} profiles)

${tableHeader}
${tableRows}

${detailedProfiles ? `\n## Detailed Summaries\n\n${detailedProfiles}\n` : ''}
---
`
}

export const LinkedinGetProfilesDisplay = createWorkflowToolComponent(
  LinkedinGetProfilesDisplayComponent,
  downloadLinkedinGetProfiles,
  linkedinGetProfilesToMarkdown
)
